import Copy from '@/components/ui/copy'
import SectionAnchor from '@/components/section-anchor'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { chainIdAtom } from '@/state/atoms'
import { ExplorerDataType, getExplorerLink } from '@/utils/getExplorerLink'
import { useAtomValue } from 'jotai'
import { ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'

const IconWrapper = ({ Component }: { Component: React.ElementType }) => (
  <div className="border rounded-full border-foreground p-2">
    <Component size={14} />
  </div>
)

// TODO: Worth to make re-usable
const InfoCard = ({
  title,
  action,
  children,
  secondary = false,
  className,
  id,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  secondary?: boolean
  className?: string
  id?: string
}) => {
  return (
    <Card
      id={id}
      className={cn(
        'rounded-4xl flex flex-col bg-secondary group/section',
        secondary && 'bg-muted'
      )}
    >
      <div className="py-4 px-5 flex items-center gap-2">
        <h1 className="font-semibold text-xl text-primary dark:text-muted-foreground">
          {title}
        </h1>
        {id && <SectionAnchor id={id} />}
        <div className="mr-auto" />
        {action}
      </div>
      <div
        className={cn(
          'bg-card mx-1 mb-1 rounded-3xl',
          secondary && 'bg-background dark:bg-card',
          className
        )}
      >
        {children}
      </div>
    </Card>
  )
}

const InfoCardItem = ({
  label,
  icon,
  value,
  className,
  address,
  bold = true,
  border = true,
  layout = 'stacked',
}: {
  label: string
  icon: React.ReactNode
  value?: React.ReactNode
  className?: string
  bold?: boolean
  address?: string
  border?: boolean
  layout?: 'stacked' | 'inline'
}) => {
  const chainId = useAtomValue(chainIdAtom)

  if (layout === 'inline') {
    return (
      <div
        className={cn(
          'flex items-center justify-between gap-4 p-4',
          className,
          border && 'border-t'
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5 text-base text-legend">
          {icon}
          <span className="truncate">{label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-right">
          {!value ? (
            <Skeleton className="h-4 w-24" />
          ) : (
            <span className={cn('text-base', bold && 'font-semibold')}>
              {value}
            </span>
          )}
          {!!address && (
            <>
              <div className="flex items-center justify-center bg-muted dark:bg-white/5 rounded-full p-1.5">
                <Copy value={address} size={10} />
              </div>
              <Link
                to={getExplorerLink(address, chainId, ExplorerDataType.ADDRESS)}
                target="_blank"
                className="p-1 bg-muted dark:bg-white/5 rounded-full"
              >
                <ArrowUpRight size={14} />
              </Link>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn('flex items-center p-4', className, border && 'border-t')}
    >
      {icon}
      <div className="ml-3 mr-auto">
        <div className="flex items-center">
          <span className="text-legend text-sm">{label}</span>
        </div>
        {!value ? (
          <Skeleton className="h-4 w-24" />
        ) : (
          <span className={cn(bold && 'font-semibold')}>{value}</span>
        )}
      </div>
      {!!address && (
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center bg-muted dark:bg-white/5 rounded-full p-1.5">
            <Copy value={address} size={10} />
          </div>
          <Link
            to={getExplorerLink(address, chainId, ExplorerDataType.ADDRESS)}
            target="_blank"
            className="p-1 bg-muted dark:bg-white/5 rounded-full"
          >
            <ArrowUpRight size={14} />
          </Link>
        </div>
      )}
    </div>
  )
}

export { InfoCard, InfoCardItem, IconWrapper }
